/**
 * Morse als Ton. (06.10.2026, Morse-Modul Bahn 1)
 *
 * Spielt einen Zeitplan (shared/morse.ts) als Sinuston — Vorgabe 600 Hz,
 * 5 ms Anstieg und Abfall gegen das Knacken. Geplant wird auf der Uhr des
 * Kontexts (`currentTime`), nicht mit Timern: ein Punkt bei 20 WpM dauert
 * 60 ms, und WebKit drosselt Timer — nur die Uhr des Kontexts hält den Takt.
 *
 * ⚠️ KEIN eigener AudioContext. Es ist der der kurzen Töne
 *    (audio-feedback.ts, `tonKontext`), und der Ton läuft durch denselben
 *    `planeTon` wie die Quittungen. app.ts hat schon drei Kontexte.
 * ⚠️ Vor dem Planen `resume()` ABWARTEN (wie `playConnectionTone`): wer auf
 *    einen schlafenden Kontext plant, hört nichts, und der Plan rutscht.
 * ⚠️ Am iPhone folgt Web Audio dem Stummschalter. Für die Dauer des Spielens
 *    steht die Tonsitzung auf `playback` — über `sitzungHalten`/`sitzungLoslassen`
 *    aus tts-pcm.ts, dem EINEN Besitzer von `navigator.audioSession` (zwei
 *    Besitzer setzten sich gegenseitig zurück).
 * ⚠️ In der App wird der Kontext danach (1,5 s nach dem letzten Ton) schlafen gelegt: ein laufender
 *    Kontext nimmt dem Mikrofon-Motor die Tonsitzung, das Wach-Wort war am
 *    02.10. darum taub (tts-pcm.ts, „DER STILLE KONTEXT NAHM DEM WACH-WORT
 *    DAS MIKROFON“). Beginn und Ende gehen an `seitenTonMelder`, damit der
 *    Motor nicht mitten im Ton neu startet.
 * ⚠️ Nicht am Gerät gehört: Stummschalter, AirPods, Sperrbildschirm und das
 *    Wach-Wort danach prüft nur der Hörtest am iPhone.
 * ⚠️ 08.10.2026 (Martin: "manchmal kommt der erste Buchstabe langsam", "Tempo mal schneller, mal langsamer"):
 *    Der Zeitplan ist exakt. Zwei Verdächtige, beide jetzt GEMESSEN statt geraten: der kalte Anlauf nach `resume()`
 *    (morse-anlauf.ts: stummes Vorspiel, erst planen, wenn die Uhr des Kontexts läuft; nur kalt, mit größerem
 *    Vorlauf) und der Takt der Kontext-Uhr gegen die der Seite. Jedes `spiele()` meldet eine Zeile
 *    `[morse-ton] ...` (morse-messung.ts, nur Konto 1, Zahlen ohne Inhalt).
 */
import type { ZeitPlan } from "../../shared/morse.js";
import { planeTon, tonKontext } from "./audio-feedback.js";
import { laufAn } from "./morse-anlauf.js";
import { meldeMorseTon, MessBegrenzer, messZeile, sinkArt, TaktMesser, type MessDaten } from "./morse-messung.js";
import { isNativePlatform } from "./native-voice.js";
import { letzterWiedergabeWeg } from "./playback-hifi.js";
import { naechsteSeitenTonId, seitenTonMelder } from "./seiten-ton.js";
import { sitzungHalten, sitzungLoslassen } from "./tts-pcm.js";

/** Lautstärke der Morse-Töne — wie die hörbaren Quittungen („gesendet“), nicht wie „achtung“. */
const MORSE_LAUTSTAERKE = 0.14;
/** Anstieg und Abfall je Ton (Sekunden). */
const MORSE_ANSTIEG_S = 0.005;
/** Abstand zwischen „jetzt“ und dem ersten Ton (Sekunden) — Luft für das Planen selbst. */
export const MORSE_VORLAUF_S = 0.08;
/**
 * Derselbe Abstand bei KALTEM Start (der Kontext schlief oder wurde gerade erst geweckt): die Uhr des Kontexts
 * läuft nach `resume()` nicht sofort — mehr Luft, damit der erste Ton nicht schon in der Vergangenheit liegt.
 */
export const MORSE_VORLAUF_KALT_S = 0.2;
/** So lange nach dem Wecken gilt ein Kontext noch als kalt (die Übung weckt ihn mit `wachHalten`, spielt kurz danach). */
export const MORSE_KALT_FENSTER_MS = 2000;
/** So lange darf `resume()` brauchen, bevor es als „kein Ton“ gilt. */
const MORSE_WECK_FRIST_MS = 1500;
/**
 * So lange nach dem letzten Ton bleibt der Kontext in der App wach. Beim Geben
 * kommen die Drücke im Abstand von Zehntelsekunden — schliefe er nach jedem,
 * müsste jeder Druck ihn erst wecken, und der Mithör-Ton käme zu spät. Dieselbe
 * Frist wie die Tonsitzung in tts-pcm.ts (1,5 s nach dem letzten Stück).
 */
export const MORSE_SCHLAF_NACH_MS = 1500;

interface MorsePlanOptionen {
  /** Tonhöhe in Hz. */
  ton: number;
  /** Beginn auf der Uhr des Kontexts (Sekunden). */
  beginn: number;
  lautstaerke?: number;
  ziel?: AudioNode;
}

/**
 * Den Zeitplan auf einen Kontext legen — rein planend, ohne Warten. Nimmt auch
 * einen `OfflineAudioContext`: so misst der Test die Zeiten am fertigen Signal.
 */
export function planeMorse(c: BaseAudioContext, plan: ZeitPlan, o: MorsePlanOptionen): OscillatorNode[] {
  return plan.toene.map((t) =>
    planeTon(c, {
      freq: o.ton,
      beginn: o.beginn + t.beginnMs / 1000,
      dauer: t.dauerMs / 1000,
      lautstaerke: o.lautstaerke ?? MORSE_LAUTSTAERKE,
      form: "flach",
      anstieg: MORSE_ANSTIEG_S,
      ziel: o.ziel,
    }),
  );
}

export type MorseSpielErgebnis = "fertig" | "abgebrochen" | "kein-ton";

/** Wartet auf `resume()` — höchstens `fristMs`. Wahr, wenn der Kontext danach läuft. */
async function wecke(c: AudioContext, fristMs: number): Promise<boolean> {
  if (c.state === "running") return true;
  let uhr: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      c.resume().catch(() => undefined),
      new Promise<void>((fertig) => {
        uhr = setTimeout(fertig, fristMs);
      }),
    ]);
  } finally {
    if (uhr !== undefined) clearTimeout(uhr);
  }
  return (c.state as string) === "running";
}

/**
 * Spielt einen Plan nach dem anderen. Ein neues `spiele()` bricht das laufende ab.
 *
 * `kontext` ist für Tests ersetzbar; ohne Angabe der Kontext der kurzen Töne.
 */
export class MorseSpieler {
  private laufend: { oszillatoren: OscillatorNode[]; ende: (e: MorseSpielErgebnis) => void } | null = null;
  /** Wie viele `spiele()` und gehaltene Töne gerade unterwegs sind — auch die, die noch auf das Wecken warten. */
  private aktiv = 0;
  private schlafUhr: ReturnType<typeof setTimeout> | null = null;
  /**
   * Zählt jedes `spiele()` und jedes `stopp()`. Ein `spiele()`, das nach dem Wecken eine andere Zahl sieht,
   * plant nichts mehr.
   *
   * ⚠️ Diagnose 06.10. (Martin: „Start und Stopp geht noch nicht so ganz“): `stopp()` kannte nur den GEPLANTEN
   *    Plan. Am iPhone braucht `resume()` seine Zeit — wer in dieser Zeit stoppte oder die Seite verließ, hörte die
   *    Aufgabe trotzdem, und zwei schnelle Tipps legten zwei Pläne übereinander (Chromium und WebKit mit 500 ms
   *    Weckzeit: 58 Töne nach dem Stopp, 116 statt 58 beim Doppeltipp).
   */
  private lauf = 0;

  /** Wann (Uhr der Seite) der Kontext zuletzt von uns geweckt wurde — die Übung weckt, `spiele()` folgt kurz danach. */
  private weckUm = Number.NEGATIVE_INFINITY;
  /** Wann der letzte Ton dieser Seite endete (Uhr der Seite) — für `leer=` in der Messzeile. */
  private letztesEnde: number | null = null;
  private readonly uhr: () => number;
  private readonly meldeZeile: (zeile: string) => void;
  private readonly weg: () => string | null;
  private readonly begrenzer = new MessBegrenzer();

  /** `mess`: Uhr, Meldeweg und Hi-Fi-Weg der Messzeile — für Tests ersetzbar (Vorgabe: Seite, Server-Journal nur Konto 1). */
  constructor(
    private readonly kontext: () => AudioContext | null = tonKontext,
    private readonly inDerApp: () => boolean = isNativePlatform,
    mess: { uhr?: () => number; melde?: (zeile: string) => void; weg?: () => string | null } = {},
  ) {
    this.uhr = mess.uhr ?? (() => performance.now());
    this.meldeZeile = mess.melde ?? meldeMorseTon;
    this.weg = mess.weg ?? letzterWiedergabeWeg;
  }

  get spielt(): boolean {
    return this.laufend !== null;
  }

  /** Der Zustand des Kontexts für die Diagnosezeile („running“, „suspended“, „interrupted“ …) — „fehlt“ ohne Web Audio. */
  zustand(): string {
    try {
      return this.kontext()?.state ?? "fehlt";
    } catch {
      return "fehlt";
    }
  }

  /**
   * Eine Übung läuft: den Kontext JETZT wecken (aus der Geste heraus — darum ohne vorheriges `await`) und
   * wach halten, bis `los()` kommt. Sonst schliefe er in der App 1,5 s nach jedem Ton, jeder Ton danach müsste ihn
   * erst wecken — beim Geben bliebe der erste Punkt nach einer Denkpause stumm (gemessen mit 150 ms Weckzeit: ein
   * Punkt von 100 ms bekam keinen Ton), beim Hören begänne jede Gruppe ohne Geste.
   * `wach`: läuft der Kontext danach? `los()` ist mehrfach rufbar.
   */
  wachHalten(): { wach: Promise<boolean>; los: () => void } {
    const c = this.kontext();
    if (!c) return { wach: Promise.resolve(false), los: () => undefined };
    this.beginne();
    let los = false;
    return {
      wach: this.wecken(c),
      los: () => {
        if (los) return;
        los = true;
        this.beende(c);
      },
    };
  }

  /**
   * Den Plan spielen. Löst auf, wenn der letzte Ton verklungen ist — oder beim Abbruch.
   * `beimStart(inMs)`: der Plan liegt auf der Uhr, der erste Ton beginnt in `inMs` — für ein Bild, das mit dem
   * Ton leuchtet (Morse-Tabelle, Kennenlernen). Kommt nur, wenn wirklich geplant wurde; ein Fehler darin stört
   * den Ton nicht.
   */
  async spiele(plan: ZeitPlan, o: { ton: number; lautstaerke?: number; beimStart?: (inMs: number) => void }): Promise<MorseSpielErgebnis> {
    this.stopp();
    const meinLauf = ++this.lauf;
    const c = this.kontext();
    if (!c || plan.toene.length === 0) return "kein-ton";
    // Kalt: der Kontext schläft oder wurde eben erst geweckt (wachHalten) — dann läuft seine Uhr noch nicht sicher.
    const vor = c.state;
    const kalt = vor !== "running" || this.uhr() - this.weckUm < MORSE_KALT_FENSTER_MS;
    const m: MessDaten = {
      kalt,
      vor,
      nach: vor,
      resumeMs: null,
      anlauf: null,
      vorlaufMs: MORSE_VORLAUF_S * 1000,
      takt: null,
      planMs: plan.gesamtMs,
      toene: plan.toene.length,
      base: null,
      out: null,
      ende: "abgebrochen",
      leerS: this.letztesEnde === null ? null : (this.uhr() - this.letztesEnde) / 1000,
      weg: null,
      sink: "-",
      ort: this.inDerApp() ? "app" : "web",
      ausgelassen: 0,
    };
    let messer: TaktMesser | null = null;
    // Erst die Tonsitzung (Stummschalter), dann wecken — beides vor dem Planen.
    this.beginne();
    sitzungHalten();
    const kennung = naechsteSeitenTonId();
    let gemeldet = false;
    try {
      const t0 = this.uhr();
      const wach = await this.wecken(c);
      if (vor !== "running") m.resumeMs = this.uhr() - t0;
      // Gestoppt oder überholt, während der Kontext aufwachte: nichts planen.
      if (meinLauf !== this.lauf) return "abgebrochen";
      if (!wach) {
        m.ende = "kein-ton";
        return "kein-ton";
      }
      let vorlaufS = MORSE_VORLAUF_S;
      if (kalt) {
        // Stilles Vorspiel, dann erst planen, wenn die Uhr des Kontexts läuft (höchstens 300 ms, danach trotzdem).
        vorlaufS = MORSE_VORLAUF_KALT_S;
        m.vorlaufMs = vorlaufS * 1000;
        m.anlauf = await laufAn(c, { uhr: this.uhr, abbruch: () => meinLauf !== this.lauf });
        if (meinLauf !== this.lauf) return "abgebrochen";
      }
      const dauerS = vorlaufS + plan.gesamtMs / 1000;
      seitenTonMelder.ttsStart(dauerS, kennung);
      gemeldet = true;
      const oszillatoren = planeMorse(c, plan, { ton: o.ton, beginn: c.currentTime + vorlaufS, lautstaerke: o.lautstaerke });
      messer = new TaktMesser(() => ({ ctxMs: c.currentTime * 1000, seiteMs: this.uhr() }));
      messer.start();
      try {
        o.beimStart?.(vorlaufS * 1000);
      } catch {
        /* ein Bild ist nie wichtiger als der Ton */
      }
      const ergebnis = await new Promise<MorseSpielErgebnis>((fertig) => {
        let erledigt = false;
        const ende = (e: MorseSpielErgebnis): void => {
          if (erledigt) return;
          erledigt = true;
          clearTimeout(nothalt);
          if (this.laufend?.ende === ende) this.laufend = null;
          fertig(e);
        };
        // Das Ende des letzten Tons — und ein Nothalt, falls `onended` ausbleibt (Kontext unterbrochen).
        oszillatoren[oszillatoren.length - 1].onended = () => ende("fertig");
        const nothalt = setTimeout(() => ende("fertig"), dauerS * 1000 + 1500);
        this.laufend = { oszillatoren, ende };
      });
      m.ende = ergebnis;
      return ergebnis;
    } finally {
      if (gemeldet) seitenTonMelder.ttsEnde(kennung);
      sitzungLoslassen();
      this.beende(c);
      this.meldeMessung(c, m, messer);
    }
  }

  /** Wecken (`resume()` abwarten, höchstens `MORSE_WECK_FRIST_MS`) und merken, wann — für "kalt" in `spiele()`. */
  private wecken(c: AudioContext): Promise<boolean> {
    if (c.state !== "running") this.weckUm = this.uhr();
    return wecke(c, MORSE_WECK_FRIST_MS);
  }

  /** Die Messzeile ans Server-Journal (morse-messung.ts). Wirft nie: eine Messung stört nie den Ton. */
  private meldeMessung(c: AudioContext, m: MessDaten, messer: TaktMesser | null): void {
    try {
      m.takt = messer?.stopp() ?? null;
      if (messer) this.letztesEnde = this.uhr();
      m.nach = c.state;
      m.base = typeof c.baseLatency === "number" ? c.baseLatency : null;
      m.out = typeof c.outputLatency === "number" ? c.outputLatency : null;
      m.sink = sinkArt(c);
      m.weg = this.weg();
      if (!this.begrenzer.darf(m.kalt, this.uhr())) return;
      m.ausgelassen = this.begrenzer.nimmAusgelassen();
      this.meldeZeile(messZeile(m));
    } catch {
      /* Diagnose ist nie wichtiger als der Ton */
    }
  }

  /**
   * Mithören beim Geben: ein Ton, solange die Taste unten ist. Gibt `loslassen`
   * zurück (mehrfach rufbar). Schläft der Kontext noch, beginnt der Ton, sobald
   * er wach ist — ist die Taste bis dahin schon los, gar nicht.
   */
  halte(o: { ton: number; lautstaerke?: number }): () => void {
    const c = this.kontext();
    if (!c) return () => undefined;
    this.beginne();
    sitzungHalten();
    const kennung = naechsteSeitenTonId();
    seitenTonMelder.ttsStart(undefined, kennung);
    let los = false;
    let ton: { osc: OscillatorNode; gain: GainNode } | null = null;
    const an = (): void => {
      if (los) return;
      try {
        const t = c.currentTime;
        const osc = c.createOscillator();
        const gain = c.createGain();
        osc.type = "sine";
        osc.frequency.value = o.ton;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(o.lautstaerke ?? MORSE_LAUTSTAERKE, t + MORSE_ANSTIEG_S);
        osc.connect(gain).connect(c.destination);
        osc.start(t);
        ton = { osc, gain };
      } catch {
        /* ohne Ton geht das Geben trotzdem */
      }
    };
    if (c.state === "running") an();
    else void this.wecken(c).then((wach) => (wach ? an() : undefined));
    return () => {
      if (los) return;
      los = true;
      const laeuft = ton;
      if (laeuft) {
        try {
          const t = c.currentTime;
          laeuft.gain.gain.cancelScheduledValues(t);
          laeuft.gain.gain.setValueAtTime(laeuft.gain.gain.value, t);
          laeuft.gain.gain.linearRampToValueAtTime(0, t + MORSE_ANSTIEG_S);
          laeuft.osc.stop(t + MORSE_ANSTIEG_S + 0.005);
        } catch {
          /* schon aus */
        }
      }
      seitenTonMelder.ttsEnde(kennung);
      sitzungLoslassen();
      this.letztesEnde = this.uhr();
      this.beende(c);
    };
  }

  private beginne(): void {
    this.aktiv += 1;
    if (this.schlafUhr) {
      clearTimeout(this.schlafUhr);
      this.schlafUhr = null;
    }
  }

  private beende(c: AudioContext): void {
    this.aktiv = Math.max(0, this.aktiv - 1);
    this.schlafen(c);
  }

  /** Sofort still — auch ein `spiele()`, das noch auf das Wecken wartet, plant danach nichts mehr. */
  stopp(): void {
    this.lauf++;
    const l = this.laufend;
    if (!l) return;
    this.laufend = null;
    for (const osc of l.oszillatoren) {
      try {
        osc.onended = null;
        osc.stop();
        osc.disconnect();
      } catch {
        /* schon gestoppt */
      }
    }
    l.ende("abgebrochen");
  }

  /**
   * In der App: den Kontext `MORSE_SCHLAF_NACH_MS` nach dem letzten Ton schlafen legen (Wach-Wort, s. o.). Wirft nie.
   * ⚠️ Nicht, solange ein neues `spiele()` oder ein gehaltener Ton unterwegs ist: das Ende des abgebrochenen Plans
   *    käme sonst NACH dem Wecken des neuen und legte ihn mitten im Planen schlafen.
   */
  private schlafen(c: AudioContext): void {
    if (!this.inDerApp() || this.aktiv > 0) return;
    if (this.schlafUhr) clearTimeout(this.schlafUhr);
    this.schlafUhr = setTimeout(() => {
      this.schlafUhr = null;
      if (this.aktiv > 0) return;
      try {
        void c.suspend().catch(() => undefined);
      } catch {
        /* egal */
      }
    }, MORSE_SCHLAF_NACH_MS);
  }
}

/** Der eine Spieler der Seite. */
export const morseSpieler = new MorseSpieler();
